import {map, distinctUntilChanged, filter} from 'rxjs/operators';
import { Component, OnInit, OnChanges, Injectable, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, ActivatedRoute, NavigationEnd, Params, PRIMARY_OUTLET, RouterLink } from "@angular/router";
import { Observable, Subject } from 'rxjs';
import { BreadcrumbService } from './breadcrumb.service';
import { AsyncPipe } from '@angular/common';



export interface IBreadcrumb {
    label: string;
    params: Params;
    url: string;
}

@Component({
    selector: "breadcrumb",
    template: `
    <ol class="breadcrumb">
      @for (breadcrumb of breadcrumbs | async; track breadcrumb) {
        <li>
          @if (breadcrumb.params) {
            <a [routerLink]="[breadcrumb.url, breadcrumb.params]">{{ breadcrumb.label }}&nbsp;</a>
          }
          @if (!breadcrumb.params) {
            <a routerLink="{{breadcrumb.url}}">{{ breadcrumb.label }}&nbsp;</a>
          }
        </li>
      }
    </ol>
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterLink, AsyncPipe]
})
export class BreadcrumbComponent {

    public breadcrumbs: Observable<IBreadcrumb[]>;

    constructor(private router: Router, private activatedRoute: ActivatedRoute, private breadcrumbService: BreadcrumbService) {

        this.breadcrumbs = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            distinctUntilChanged(),
            map(event => this.buildBreadCrumb(this.activatedRoute.root)),);
    }

    private parmsObject = {};

    buildBreadCrumb(route: ActivatedRoute, url: string = '', breadcrumbs: IBreadcrumb[] = []): IBreadcrumb[] {

        if (route.routeConfig && route.routeConfig.data && route.routeConfig.data['breadcrumb']) {

            let label = route.routeConfig.data['breadcrumb'];
            let path = route.routeConfig.path;

            // To prevent error: 'Root segment cannot have matrix parameters' - set the params object to undefined if theres no parameters. And check for this in the HTML.
            let params = Object.keys(route.snapshot.params).length === 0 ? undefined : route.snapshot.params;

            // We need to store the parameters for each NavigationEnd event, because it otherwise will be lost (for some reason the params object is empty on child snapshots)
            if (params) {
                this.parmsObject[path] = params;
            }

            // Get the stored parameters if needed. 
            // TODO: This can in some scenarios result in wrong parameters for the breadcrumb, if we somehow navigate to a page with parameters and then come back to this page without parameters. FIX THIS!
            if (!params) {
                params = this.parmsObject[path];
            }

            //In the routeConfig the complete path is not available,
            //so we rebuild it each time
            let nextUrl = `${url}${path}/`;
            let breadcrumb: IBreadcrumb = {
                label: label,
                params: params,
                url: nextUrl
            };

            breadcrumbs.push(breadcrumb);
            if (route.firstChild) {
                //If we are not on our current path yet, 
                //there will be more children to look after, to build our breadcumb
                return this.buildBreadCrumb(route.firstChild, nextUrl, breadcrumbs);
            }
        }
        else {
            if (route.firstChild) {
                //If we are not on our current path yet, 
                //there will be more children to look after, to build our breadcumb
                return this.buildBreadCrumb(route.firstChild, '', breadcrumbs);
            }
        }
        this.breadcrumbService.breadcrumbChanged(breadcrumbs);
        return breadcrumbs;
    }
}